import { FormEvent, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ErrorResponse } from 'react-router-dom'
import { useBoolean } from 'react-use'
import isValidDomain from 'is-valid-domain'

import { useFormEnter } from '@/shared/makyo-hooks'

import { useAddCustomDomainMutation } from '@/resolvers/project/mutation'
import { useSingleProjectContext } from '@/context/SingleProjectContext'
import { useReqError } from '@/hooks/useReqError'

/**
 * `useDomainField` is a custom React hook that manages the state of a
 * domain field and its related functionalities such as validation,
 * submission, and error handling. This hook is mainly used for adding
 * a new custom domain.
 *
 * @returns {Object} A stateful value and a set of methods.
 *
 * @property {Function} t - The translation function from useTranslation hook.
 * @property {string} domain - The current value of the domain.
 * @property {string|undefined} error - The current error message.
 * @property {string} formId - The id of the form.
 * @property {boolean} disableSubmit - A flag indicating whether the submit button should be disabled.
 * @property {boolean} isLoading - A flag indicating whether the form is currently being submitted.
 * @property {Function} setDomain - A function to update the domain.
 * @property {Function} setError - A function to set an error message.
 * @property {Function} reset - A function to reset the domain field.
 * @property {Function} isExists - A function to check if a domain already exists.
 * @property {Function} handleSubmit - A function to handle form submission.
 */
const useDomainField = () => {
  const { t } = useTranslation()
  const { project, updateProject } = useSingleProjectContext()
  const [domain, setDomain] = useState('')
  const [error, setError] = useState<string | undefined>(undefined)
  const addCustomDomain = useAddCustomDomainMutation()

  const reset = () => {
    setDomain('')
  }

  const isExists = (d: string) => project?.domains.find((a) => a.domain === d)
  // 'new-domain' is the id of the form
  const formId = 'new-domain'

  // disableSubmit is a memoized value that returns true if there is an error or if the domain is not valid.
  const disableSubmit = useMemo(() => {
    return error !== undefined || !isValidDomain(domain, { subdomain: true })
  }, [domain, error])

  // printAPIError is a function that displays API errors
  const printAPIError = useReqError()

  // isLoading is a boolean that indicates whether a request is currently being made. setIsLoading is a function to toggle isLoading.
  const [isLoading, setIsLoading] = useBoolean(false)

  // handleSubmit is a function that is called when the form is submitted.
  const handleSubmit = useCallback(
    async (e: Event | FormEvent) => {
      // Prevent the default form submission behavior
      e.preventDefault()

      // If the submit button is disabled or a request is currently being made, exit the function.
      if (disableSubmit || isLoading) {
        return
      }

      // If there is no project data, exit the function.
      if (!project) {
        return
      }

      // Indicate that a request is being made
      setIsLoading(true)

      // Attempt to add the custom domain
      addCustomDomain
        .mutateAsync({
          projectId: project.id,
          customDomain: domain,
        })
        .then((res) => {
          // If the domain was added successfully, update the project data and reset the form.
          updateProject({
            ...project,
            domains: [
              ...project.domains,
              {
                id: res._id,
                domain,
                verified: false,
              },
            ],
          })
          reset()
        })
        .catch((e) => {
          // If an error occurred, check if the domain was already taken.
          const err = e as ErrorResponse & Error
          if (err?.message.includes('taken')) {
            setError(err.message)
          } else {
            // If the error is not related to the domain being taken, print the API error.
            printAPIError(err)
          }
        })
        .finally(() => {
          // Indicate that the request has finished.
          setIsLoading(false)
        })
    },
    [domain, project, disableSubmit, printAPIError, updateProject, isLoading, setIsLoading]
  )

  useFormEnter(formId, handleSubmit)

  return {
    t,
    domain,
    error,
    formId,
    disableSubmit,
    isLoading,
    setDomain,
    setError,
    reset,
    isExists,
    handleSubmit,
  }
}

export default useDomainField
